import MapboxDraw, { constants, DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { Point } from "mapbox-gl";

import * as turf from "fond/turf";

import readOnlySelect from "../base/readOnlySelect";

import { addDrawFeature, TOO_MANY_FEATURES_THRESHOLD } from "./helper";

const {
  lib: {
    CommonSelectors: { isFeature, noTarget, isOfMetaType, isShiftDown },
  },
} = MapboxDraw;

/**
 * Returns a Point representing a mouse event's position
 * relative to a containing element.
 *
 * @param {MouseEvent} mouseEvent
 * @param {HTMLElement} container
 * @returns {Point}
 */
function mouseEventPoint(mouseEvent: MouseEvent, container: HTMLElement) {
  const rect = container.getBoundingClientRect();
  return new Point(mouseEvent.clientX - rect.left - (container.clientLeft || 0), mouseEvent.clientY - rect.top - (container.clientTop || 0));
}

/**
 * This is a custom draw mode that allows for the user to select existing draw features on the map
 * or to select features from the a Cost to Serve relevant layer (e.g. "input_layers/base_data/sl").
 *
 * Selection of Cost to Serve layer features is done either by clicking on the feature or by box selecting.
 *
 * Selected features cannot be moved in this mode.
 */
const ctsSelect: DrawCustomMode = {
  ...readOnlySelect,

  /**
   * This is a custom onClick function to handle the creation of drawFeatures when clicking on a feature that
   * is sourced from a vector layer (i.e. not an existing draw feature)
   */
  onClick: function (state, event) {
    // Due to features within a vector tile project noTarget could mean the user
    // clicked on an empty area of the map or clicked on a feature within a vector tile layer.
    if (noTarget(event)) {
      // If no feature is targeted and shift is not held down, clear the selection
      if (!isShiftDown(event)) {
        this.clearSelectedFeatures();
      }

      // Get all features within a buffer around the click location.
      const queryBbox = [
        this.map.unproject([event.point.x - 5, event.point.y - 5]).toArray(),
        this.map.unproject([event.point.x + 5, event.point.y + 5]).toArray(),
      ].flat();
      const { bbox, ...polygon } = turf.bboxPolygon(queryBbox);
      const features = this._ctx.options.getFeaturesInBoundary?.(polygon) || [];

      // Create the new drawing feature for each vector tile feature found, then select them.
      const newFeatures = features.map((feature) => {
        return this.addDrawFeature?.(feature);
      });
      newFeatures.forEach((f) => {
        if (f?.id) this.select(f.id);
      });

      // If no new features were created, treat this as a clickAnywhere event (which may clear selection)
      if (newFeatures.length === 0) {
        return this.clickAnywhere?.(state, event);
      }
    }

    if (isOfMetaType(constants.meta.VERTEX)(event)) return this.clickOnVertex?.(state, event); // tap
    if (isFeature(event) && this.map) {
      // Cost to Serve commonly has collocated features, so we need to select all nearby features on click
      // based on the location of the top feature clicked.
      const queryBbox = [
        this.map.unproject([event.point.x - 5, event.point.y - 5]).toArray(),
        this.map.unproject([event.point.x + 5, event.point.y + 5]).toArray(),
      ].flat();
      const { bbox, ...polygon } = turf.bboxPolygon(queryBbox);
      const features = this._ctx.options.getFeaturesInBoundary?.(polygon) || [];

      // Select all the features found
      features.forEach((f) => {
        if (f.id) this.select(f.id);
      });
    }

    return undefined;
  },

  /**
   * A custom onMouseUp to handle the creation of drawFeatures when box selecting
   */
  onMouseUp: function (state, event) {
    // End any extended interactions
    if (state.dragMoving) {
      this.fireUpdate?.();
    } else if (state.boxSelecting) {
      // Get all the features that intersect with the box selection area
      // For all features we call addDrawFeature to create the corresponding draw features and set them to selected.
      const startLngLat = this.map.unproject(state.boxSelectStartLocation);
      const endLngLat = this.map.unproject(mouseEventPoint(event.originalEvent, this.map.getContainer()));
      const { bbox, ...polygon } = turf.bboxPolygon([startLngLat.lng, startLngLat.lat, endLngLat.lng, endLngLat.lat]);

      const features = this._ctx.options.getFeaturesInBoundary?.(polygon) || [];
      features.forEach((feature) => {
        this.addDrawFeature?.(feature);
      });

      // We limit the number of features that can be selected at once since selecting a large number of features can cause performance issues.
      const idsToSelect = features.map((f) => f.id).filter((id) => id && !this.isSelected(id)) as string[];
      if (idsToSelect.length > TOO_MANY_FEATURES_THRESHOLD) {
        this._ctx.options.onSelectionLimitExceeded?.(idsToSelect.length);
      } else if (idsToSelect.length) {
        this.select(idsToSelect);
      }
    }
    this.stopExtendedInteractions?.(state);
  },

  addDrawFeature,
} as DrawCustomMode;

export default ctsSelect;
